import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, Chip, Alert,
  CircularProgress, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip
} from '@mui/material';
import { CalendarToday, Search, MedicalServices, EventNote, Print, AddCircle, ExitToApp } from '@mui/icons-material';
import { dayCareAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

function DayCarePatientsList() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isReception = user.role === 'reception';
  const [registrations, setRegistrations] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [dailyPlanDialog, setDailyPlanDialog] = useState({ open: false, reg: null, date: new Date().toISOString().split('T')[0] });
  const [dailyPlanData, setDailyPlanData] = useState(null);
  const [dailyPlanLoading, setDailyPlanLoading] = useState(false);

  const [extendDialog, setExtendDialog] = useState({ open: false, reg: null, additionalDays: 1 });
  const [extendLoading, setExtendLoading] = useState(false);
  const [dischargeDialog, setDischargeDialog] = useState({ open: false, reg: null });
  const [dischargeLoading, setDischargeLoading] = useState(false);
  const dailyPlanPrintRef = useRef();
  const handlePrintDailyPlan = useReactToPrint({
    contentRef: dailyPlanPrintRef,
    documentTitle: 'Day Care Daily Plan',
    pageStyle: '@page { size: A4 portrait; margin: 10mm; }'
  });

  useEffect(() => { loadRegistrations(); }, []);
  useEffect(() => { filterList(); }, [searchQuery, registrations]);

  const loadRegistrations = async () => {
    setLoading(true);
    try {
      const response = await dayCareAPI.getActiveRegistrations();
      const data = response.data.data || response.data;
      setRegistrations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load day care patients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterList = () => {
    if (!searchQuery.trim()) { setFiltered(registrations); return; }
    const q = searchQuery.toLowerCase();
    setFiltered(registrations.filter(r =>
      r.patient_code?.toLowerCase().includes(q) ||
      r.first_name?.toLowerCase().includes(q) ||
      r.last_name?.toLowerCase().includes(q)
    ));
  };

  const handleManage = (reg) => {
    navigate(`/doctor/day-care/${reg.id}`, {
      state: {
        patientName: `${reg.first_name} ${reg.last_name}`,
        patientCode: reg.patient_code,
        patientId: reg.patient_id,
        admissionDate: reg.start_date,
        plannedDays: reg.planned_days
      }
    });
  };

  const handleExtend = async () => {
    setExtendLoading(true);
    try {
      await dayCareAPI.extendDuration(extendDialog.reg.id, { additionalDays: extendDialog.additionalDays });
      setExtendDialog({ open: false, reg: null, additionalDays: 1 });
      loadRegistrations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to extend duration');
    } finally {
      setExtendLoading(false);
    }
  };

  const handleDischarge = async () => {
    setDischargeLoading(true);
    try {
      await dayCareAPI.discharge(dischargeDialog.reg.id);
      setDischargeDialog({ open: false, reg: null });
      loadRegistrations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to discharge patient');
    } finally {
      setDischargeLoading(false);
    }
  };

  const openDailyPlan = (reg) => {
    setDailyPlanData(null);
    setDailyPlanDialog({ open: true, reg, date: new Date().toISOString().split('T')[0] });
  };

  const fetchAndPrintDailyPlan = async () => {
    setDailyPlanLoading(true);
    try {
      const res = await dayCareAPI.getDailyPlan(dailyPlanDialog.reg.id, dailyPlanDialog.date);
      setDailyPlanData(res.data.data);
      setTimeout(() => handlePrintDailyPlan(), 300);
    } catch (err) {
      console.error('Failed to load daily plan', err);
    } finally {
      setDailyPlanLoading(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;

  return (
    <Card elevation={2}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <MedicalServices sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
          <Typography variant="h5" component="h2" fontWeight="bold">
            Day Care Patients
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

        <TextField
          fullWidth
          placeholder="Search by patient code or name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
          }}
        />

        {filtered.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography color="text.secondary">
              {searchQuery ? 'No patients found matching your search' : 'No active day care patients'}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>UHID</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Start Date</strong></TableCell>
                  <TableCell align="right"><strong>Days Attended</strong></TableCell>
                  <TableCell align="right"><strong>Planned Days</strong></TableCell>
                  <TableCell align="right"><strong>Daily Charge</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((reg) => (
                  <TableRow key={reg.id} hover>
                    <TableCell>{reg.patient_code}</TableCell>
                    <TableCell>{reg.first_name} {reg.last_name}</TableCell>
                    <TableCell>{new Date(reg.start_date).toLocaleDateString('en-GB')}</TableCell>
                    <TableCell align="right">
                      <Chip label={reg.days_attended} size="small" color="default" />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={reg.planned_days} size="small" color="secondary" />
                    </TableCell>
                    <TableCell align="right">₹{parseFloat(reg.daily_charge || 0).toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title="Print Daily Treatment & Diet Plan">
                          <Button variant="outlined" size="small" color="secondary" startIcon={<EventNote />}
                            onClick={() => openDailyPlan(reg)}>
                            Daily Plan
                          </Button>
                        </Tooltip>
                        <Tooltip title="Extend Day Care Duration">
                          <Button variant="outlined" size="small" color="primary" startIcon={<AddCircle />}
                            onClick={() => setExtendDialog({ open: true, reg, additionalDays: 1 })}>
                            Extend
                          </Button>
                        </Tooltip>
                        <Tooltip title="Discharge Patient">
                          <Button variant="outlined" size="small" color="error" startIcon={<ExitToApp />}
                            onClick={() => setDischargeDialog({ open: true, reg })}>
                            Discharge
                          </Button>
                        </Tooltip>
                        {!isReception && (
                          <Button variant="contained" size="small" color="secondary"
                            onClick={() => handleManage(reg)} sx={{ minWidth: 120 }}>
                            Manage Patient
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <CalendarToday fontSize="small" />
          <Typography variant="body2">Total Active: {filtered.length}</Typography>
        </Box>
      </CardContent>

      {/* Extend Duration dialog */}
      <Dialog open={extendDialog.open} onClose={() => setExtendDialog({ open: false, reg: null, additionalDays: 1 })} maxWidth="xs" fullWidth>
        <DialogTitle>Extend Day Care Duration</DialogTitle>
        <DialogContent>
          {extendDialog.reg && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Patient: <strong>{extendDialog.reg.first_name} {extendDialog.reg.last_name}</strong><br />
              Current planned days: <strong>{extendDialog.reg.planned_days}</strong>
            </Typography>
          )}
          <TextField
            fullWidth label="Additional Days" type="number"
            value={extendDialog.additionalDays}
            onChange={e => setExtendDialog(d => ({ ...d, additionalDays: Math.max(1, parseInt(e.target.value) || 1) }))}
            inputProps={{ min: 1 }}
          />
          {extendDialog.reg && extendDialog.additionalDays > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              New total: {extendDialog.reg.planned_days + extendDialog.additionalDays} days
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialog({ open: false, reg: null, additionalDays: 1 })}>Cancel</Button>
          <Button variant="contained" color="primary"
            onClick={handleExtend} disabled={extendLoading || extendDialog.additionalDays < 1}>
            {extendLoading ? <CircularProgress size={20} color="inherit" /> : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discharge Confirm dialog */}
      <Dialog open={dischargeDialog.open} onClose={() => setDischargeDialog({ open: false, reg: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Discharge Patient</DialogTitle>
        <DialogContent>
          {dischargeDialog.reg && (
            <Typography variant="body2">
              Are you sure you want to discharge <strong>{dischargeDialog.reg.first_name} {dischargeDialog.reg.last_name}</strong> from Day Care?
              <br /><br />
              This will mark the registration as completed and set today as the discharge date.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDischargeDialog({ open: false, reg: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDischarge} disabled={dischargeLoading}>
            {dischargeLoading ? <CircularProgress size={20} color="inherit" /> : 'Discharge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Daily Plan dialog */}
      <Dialog open={dailyPlanDialog.open} onClose={() => setDailyPlanDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Print Daily Treatment & Diet Plan</DialogTitle>
        <DialogContent>
          {dailyPlanDialog.reg && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Patient: <strong>{dailyPlanDialog.reg.first_name} {dailyPlanDialog.reg.last_name}</strong>
            </Typography>
          )}
          <TextField
            fullWidth label="Date" type="date"
            value={dailyPlanDialog.date}
            onChange={e => setDailyPlanDialog(d => ({ ...d, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDailyPlanDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" startIcon={dailyPlanLoading ? <CircularProgress size={16} color="inherit" /> : <Print />}
            onClick={fetchAndPrintDailyPlan} disabled={dailyPlanLoading}>
            {dailyPlanLoading ? 'Loading...' : 'Print'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Daily plan print portal */}
      {dailyPlanData && createPortal(
        <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '190mm' }}>
          <div ref={dailyPlanPrintRef} style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', background: 'white', padding: '8mm 10mm' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 10 }}>
              <img src="/logo.png" alt="MSG Logo" style={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
                <div style={{ fontSize: 9, color: '#444' }}>Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351</div>
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3 }}>DAY CARE — DAILY PLAN</div>
              </div>
            </div>

            {/* Patient info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px', marginBottom: 10, fontSize: 10 }}>
              <div><strong>Patient:</strong> {dailyPlanData.patient.first_name} {dailyPlanData.patient.last_name}</div>
              <div><strong>UHID:</strong> {dailyPlanData.patient.patient_code}</div>
              <div><strong>Phone:</strong> {dailyPlanData.patient.phone || '—'}</div>
              <div><strong>Date:</strong> {new Date(dailyPlanDialog.date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
            </div>

            {/* Vitals */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ background: '#fce4ec', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderLeft: '4px solid #c62828', marginBottom: 4 }}>
                Vitals ({(dailyPlanData.vitals || []).length} {(dailyPlanData.vitals || []).length === 1 ? 'record' : 'records'})
              </div>
              {!(dailyPlanData.vitals || []).length ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No vitals recorded for this date.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      {['Time', 'BP', 'Temp', 'Pulse', 'RR', 'SpO2%', 'Wt (kg)', 'RBS', 'Insulin', 'Pain'].map(h => (
                        <th key={h} style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(dailyPlanData.vitals || []).map((v, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', textAlign: 'center' }}>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{new Date(v.recorded_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.blood_pressure || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.temperature || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.pulse_rate || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.respiratory_rate || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.oxygen_saturation || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.weight || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.rbs || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.insulin_units || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600 }}>{v.pain_scale ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Treatments */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ background: '#e8f5e9', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderLeft: '4px solid #2e7d32', marginBottom: 4 }}>
                Treatment Plan ({[...new Set(dailyPlanData.treatments.map(t => t.time_slot))].length} slots, {dailyPlanData.treatments.length} items)
              </div>
              {dailyPlanData.treatments.length === 0 ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No treatments scheduled for this date.</div>
              ) : (() => {
                const slotOrder = ['Satkriya', 'Morning', 'Evening', 'Night'];
                const grouped = {};
                dailyPlanData.treatments.forEach(t => {
                  const slot = t.time_slot || 'Other';
                  if (!grouped[slot]) grouped[slot] = [];
                  grouped[slot].push(t);
                });
                const slots = [...slotOrder.filter(s => grouped[s]), ...Object.keys(grouped).filter(s => !slotOrder.includes(s))];
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        {['Time Slot', 'Treatments'].map(h => (
                          <th key={h} style={{ border: '1px solid #ddd', padding: '3px 6px', textAlign: 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {slots.map((slot, i) => (
                        <tr key={slot} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ border: '1px solid #ddd', padding: '3px 6px', whiteSpace: 'nowrap' }}>{slot}</td>
                          <td style={{ border: '1px solid #ddd', padding: '3px 6px', fontWeight: 600 }}>
                            {grouped[slot].map(t => t.treatment_name).join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            {/* Diet */}
            <div>
              <div style={{ background: '#fff8e1', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderLeft: '4px solid #f57f17', marginBottom: 4 }}>
                Diet Plan ({[...new Set(dailyPlanData.meals.map(m => m.meal_time))].length} meal times, {dailyPlanData.meals.length} items)
              </div>
              {dailyPlanData.meals.length === 0 ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No diet plan scheduled for this date.</div>
              ) : (() => {
                const mealOrder = ['7AM', 'Breakfast', '11AM', 'Lunch', '1PM', '5PM', 'Dinner'];
                const grouped = {};
                dailyPlanData.meals.forEach(m => {
                  const meal = m.meal_time || 'Other';
                  if (!grouped[meal]) grouped[meal] = [];
                  grouped[meal].push(m);
                });
                const meals = [...mealOrder.filter(m => grouped[m]), ...Object.keys(grouped).filter(m => !mealOrder.includes(m))];
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        {['Meal Time', 'Diet Items', 'Instructions'].map(h => (
                          <th key={h} style={{ border: '1px solid #ddd', padding: '3px 6px', textAlign: 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {meals.map((meal, i) => {
                        const items = grouped[meal];
                        return (
                          <tr key={meal} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ border: '1px solid #ddd', padding: '3px 6px', whiteSpace: 'nowrap' }}>{meal}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 6px', fontWeight: 600 }}>
                              {items.map(m => m.quantity ? `${m.diet_item_name} (${m.quantity})` : m.diet_item_name).join(', ')}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 6px', color: '#555' }}>
                              {items.map(m => m.special_instructions).filter(Boolean).join('; ') || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 6, textAlign: 'center', fontSize: 9, color: '#888' }}>
              MSG Naturopathy Center — Printed: {new Date().toLocaleString('en-IN')}
            </div>
          </div>
        </div>,
        document.body
      )}
    </Card>
  );
}

export default DayCarePatientsList;
